import { useSyncExternalStore } from 'react'

/** Client-side cache of who sent the latest message per conversation (API does not expose this yet). */
const lastSenderByConversation = new Map<string, string>()
const optimisticReadIds = new Set<string>()
const listeners = new Set<() => void>()
let version = 0

function emit() {
  version += 1
  for (const listener of listeners) listener()
}

export function setLastMessageSender(conversationId: string, senderId: string): void {
  lastSenderByConversation.set(conversationId, senderId)
}

/**
 * Sign-out hygiene: this module-level cache outlives the session (no page
 * reload on sign-out), so drop the previous account's conversation/sender ids
 * alongside the directory caches.
 */
export function resetLastMessageSenderCache(): void {
  lastSenderByConversation.clear()
  optimisticReadIds.clear()
  emit()
}

/**
 * Record a live incoming message. Unlike {@link setLastMessageSender} (which
 * also runs when hydrating stale server snapshots), this is only called for
 * realtime message events — so a message from another user invalidates any
 * optimistic "read" flag for that conversation. Without this, a thread opened
 * once could keep its optimistic flag while new messages arrive, permanently
 * suppressing its unread badge (the flag is otherwise only cleared when the
 * server reports `unread: false`, which never happens for a hidden badge the
 * user has no reason to open).
 */
export function noteIncomingMessage(
  conversationId: string,
  senderId: string,
  currentUserId: string | undefined,
): void {
  setLastMessageSender(conversationId, senderId)
  if (currentUserId && senderId !== currentUserId) {
    clearThreadReadOptimistic(conversationId)
  }
}

export function getLastMessageSender(conversationId: string): string | undefined {
  return lastSenderByConversation.get(conversationId)
}

/** Client-side read state until the server confirms lastReadAt >= lastMessageAt. */
export function markThreadReadOptimistic(conversationId: string): void {
  optimisticReadIds.add(conversationId)
  emit()
}

export function clearThreadReadOptimistic(conversationId: string): void {
  if (!optimisticReadIds.delete(conversationId)) return
  emit()
}

export function subscribeThreadReadStore(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function getThreadReadVersion(): number {
  return version
}

/**
 * Re-render subscription for memoized rows whose unread state derives from
 * this store (optimistic reads / last-sender). Memoized thread rows would
 * otherwise keep a stale unread dot when the store changes underneath them.
 */
export function useThreadReadVersion(): number {
  return useSyncExternalStore(subscribeThreadReadStore, getThreadReadVersion, getThreadReadVersion)
}

type UnreadThread = {
  id?: string
  conversationId?: string
  type?: string | null
  unread?: boolean | null
  lastMessageSenderId?: string | null
}

/** Wall-style announcement channels use a separate read model — never count as DM unread. */
export function isMessagingConversationType(type: string | null | undefined): boolean {
  return type === 'DIRECT' || type === 'GROUP'
}

function resolveThreadId(thread: UnreadThread): string | undefined {
  return thread.id ?? thread.conversationId
}

function resolveLastSender(thread: UnreadThread): string | undefined {
  const threadId = resolveThreadId(thread)
  return (
    thread.lastMessageSenderId ??
    (threadId ? getLastMessageSender(threadId) : undefined) ??
    undefined
  )
}

/** Treat self-sent and optimistically read threads as read even when the server flag lags. */
export function isThreadEffectivelyUnread(
  thread: UnreadThread,
  currentUserId: string | undefined,
): boolean {
  if (thread.type && !isMessagingConversationType(thread.type)) return false
  if (!thread.unread) {
    const threadId = resolveThreadId(thread)
    if (threadId) clearThreadReadOptimistic(threadId)
    return false
  }
  const threadId = resolveThreadId(thread)
  if (threadId && optimisticReadIds.has(threadId)) return false
  if (currentUserId && resolveLastSender(thread) === currentUserId) return false
  return true
}

export function isConversationUnreadForUser(
  c: UnreadThread & { conversationId: string },
  userId: string | undefined,
): boolean {
  return isThreadEffectivelyUnread(c, userId)
}
